export const MAX_SAVED_ADDRESSES = 4;

export const SAVED_ADDRESS_LIMIT_MESSAGE =
  'You can save up to 4 delivery addresses. Delete one of your existing addresses to add a new one.';

// NOTE: The saved-address cap is temporarily disabled by request. To re-apply the
// limit, restore the body to `return count < MAX_SAVED_ADDRESSES;`.
/**
 * @param {number} count
 * @returns {boolean}
 */
export function canAddSavedAddress(count) {
  void count;
  return true;
}

/**
 * @param {Array<{ id: number | null, isPrimary?: boolean }>} addresses
 * @param {{ selectedId?: number | null }} [options]
 * @returns {number | null}
 */
export function chooseCanonicalPrimaryId(addresses, options = {}) {
  const { selectedId = null } = options;
  const primaries = addresses.filter((a) => a.isPrimary && a.id != null);
  if (primaries.length === 0) return null;
  if (primaries.length === 1) return primaries[0].id;
  const selectedPrimary = primaries.find((a) => a.id === selectedId);
  if (selectedPrimary) return selectedPrimary.id;
  return primaries.reduce((min, a) => (Number(a.id) < Number(min.id) ? a : min)).id;
}

/**
 * @param {object} address normalized address row
 * @param {{ isPrimary?: boolean }} [overrides]
 */
export function addressToUpdatePayload(address, overrides = {}) {
  return {
    id: Number(address.id),
    name: String(address.name || '').trim(),
    type: Number(address.type),
    address: String(address.address || '').trim(),
    city: String(address.city || '').trim(),
    state: String(address.state || '').trim(),
    country: String(address.country || '').trim() || 'India',
    postalCode: String(address.postalCode || '').trim(),
    isPrimary: overrides.isPrimary ?? Boolean(address.isPrimary),
    latitude: Number(address.latitude),
    longitude: Number(address.longitude),
  };
}

/**
 * @param {(path: string, opts?: { method?: string, body?: object }) => Promise<unknown>} requestAddressApi
 * @param {Array<object>} addresses
 * @param {number} keepPrimaryId
 */
export async function demoteExtraPrimaryAddresses(requestAddressApi, addresses, keepPrimaryId) {
  const toDemote = addresses.filter((a) => a.isPrimary && a.id !== keepPrimaryId);
  for (const addr of toDemote) {
    await requestAddressApi('/auth/updateUserAddress', {
      method: 'POST',
      body: addressToUpdatePayload(addr, { isPrimary: false }),
    });
  }
  return toDemote.length;
}

/**
 * @param {Array<object>} list
 * @param {{ id?: number | null, name?: string, address?: string, postalCode?: string }} form
 * @param {boolean} editing
 */
export function resolveSavedAddressIdAfterSubmit(list, form, editing) {
  if (editing && form.id != null) return Number(form.id);
  const name = String(form.name || '').trim();
  const address = String(form.address || '').trim();
  const postalCode = String(form.postalCode || '').trim();
  const match = list.find(
    (a) =>
      String(a.name || '').trim() === name &&
      String(a.address || '').trim() === address &&
      String(a.postalCode || '').trim() === postalCode,
  );
  if (match?.id != null) return match.id;
  const ids = list.map((a) => Number(a.id)).filter(Number.isFinite);
  return ids.length ? Math.max(...ids) : null;
}

/**
 * @param {Array<{ id: number | null, latitude: number | null, longitude: number | null }>} addresses
 * @param {number | null} selectedId
 */
export function findSelectedDeliveryAddress(addresses, selectedId) {
  if (selectedId == null) return null;
  const targetId = Number(selectedId);
  if (!Number.isFinite(targetId)) return null;
  const row = addresses.find((a) => Number(a.id) === targetId);
  if (!row) return null;
  if (!Number.isFinite(row.latitude) || !Number.isFinite(row.longitude)) return null;
  return row;
}
